import { memo, useMemo, useEffect } from 'react';

import clsx from 'clsx';
import {
  Control,
  useController,
  UseFormSetValue,
  UseFormClearErrors,
  useWatch
} from 'react-hook-form';

import {
  AdaptiveFormConfig,
  INTERACTIVE_FIELD_TYPES,
  getClearValueForFieldType,
  Fields
} from '@elements';
import { HealthInsuredMemberBasic } from '@store/proposal/types';
import { Group, Text, Radio, Stack } from '@ui';

import { AccordionSection } from '../accordionSection';

import classes from './MembersAccordion.module.css';

export type MemberAccordionItemProps = {
  member: HealthInsuredMemberBasic;
  memberIndex: number;
  name: string;
  onSelectKey: string;
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  clearErrors: UseFormClearErrors<any>;
  subQuestionsConfig: AdaptiveFormConfig;
  hasError: boolean;
  memberIdsWithDisease?: string[];
  showMemberRadios?: boolean;
};

// Extract switch field names from config - moved outside component to avoid recreation
const extractSwitchFieldNames = (
  config: AdaptiveFormConfig,
  name: string,
  memberIndex: number
): string[] => {
  const fieldNames: string[] = [];
  const namePattern = `${name}[${memberIndex}].`;

  const extractFromFields = (fields: any[]) => {
    fields?.forEach((field: any) => {
      if (field.type === Fields.switchGroup && field.fields) {
        field.fields.forEach((switchField: any) => {
          if (switchField.type === Fields.switch && switchField.name) {
            if (switchField.name.startsWith(namePattern)) {
              fieldNames.push(switchField.name.replace(namePattern, ''));
            }
          }
        });
      }
    });
  };

  config?.forEach((section: any) => {
    if (section.type && section.fields) {
      extractFromFields([section]);
    } else if (section.fields) {
      extractFromFields(section.fields);
    } else if (section.type) {
      extractFromFields([section]);
    }
  });

  return fieldNames;
};

// Check if config contains only switch questions - moved outside to avoid recreation
const checkIfOnlySwitchQuestions = (config: AdaptiveFormConfig): boolean => {
  if (!config?.length) return false;

  const checkFields = (fields: any[]): boolean => {
    return fields.every((field) => {
      if (field.type === Fields.switchGroup) {
        return field.fields?.every(
          (nestedField: any) => nestedField.type === Fields.switch
        );
      }
      if (field.type === Fields.switch) {
        return true;
      }
      return false;
    });
  };

  return config.every((section: any) => {
    if (section.type && section.fields) {
      return checkFields([section]);
    } else if (section.fields) {
      return checkFields(section.fields);
    } else if (section.type) {
      return checkFields([section]);
    }
    return false;
  });
};

// Handle radio change - extracted to reduce inline logic
const handleRadioChangeFactory = (
  field: any,
  setValue: UseFormSetValue<any>,
  clearErrors: UseFormClearErrors<any>,
  showMemberRadios: boolean,
  subQuestionsConfig: AdaptiveFormConfig,
  name: string,
  memberIndex: number,
  onSelectKey: string
) => {
  return (value: string) => {
    if (value === 'true') {
      field.onChange(true);
    } else if (value === 'false') {
      field.onChange(false);

      // Only clear fields when showMemberRadios is true (manual radio selection)
      if (showMemberRadios && subQuestionsConfig?.length) {
        // Clear errors for this member
        const questionKey = onSelectKey.replace('.answer', '');
        clearErrors(`${name}.${memberIndex}.${questionKey}`);
        clearErrors(`${name}.${memberIndex}.${onSelectKey}`);

        // Clear all field values
        const fieldsToClear: { name: string; clearValue: any }[] = [];

        const collectField = (field: any) => {
          if (INTERACTIVE_FIELD_TYPES.includes(field.type) && field.name) {
            const clearValue = getClearValueForFieldType(field.type);
            fieldsToClear.push({ name: field.name, clearValue });
          }
          if (field.fields) {
            field.fields.forEach(collectField);
          }
        };

        subQuestionsConfig.forEach((section: any) => {
          if (section.type) {
            collectField(section);
          } else if (section.fields) {
            section.fields.forEach(collectField);
          }
        });

        if (fieldsToClear.length > 0) {
          fieldsToClear.forEach(({ name, clearValue }) => {
            setValue(name, clearValue, {
              shouldValidate: false,
              shouldDirty: false
            });
          });
        }
      }
    }
  };
};

export const MemberAccordionItem = memo(
  ({
    member,
    memberIndex,
    name,
    onSelectKey,
    control,
    setValue,
    clearErrors,
    subQuestionsConfig,
    hasError,
    memberIdsWithDisease,
    showMemberRadios = false
  }: MemberAccordionItemProps) => {
    const noSubQuestions = !subQuestionsConfig?.length;

    // Use controller for the radio group
    const { field, fieldState } = useController({
      name: `${name}[${memberIndex}].${onSelectKey}`,
      control
    });

    const isRadioNo = showMemberRadios && field.value === false;

    // Only compute switch-related stuff when showMemberRadios is false
    const switchFieldNames = !showMemberRadios
      ? extractSwitchFieldNames(subQuestionsConfig, name, memberIndex)
      : [];

    // Only watch when showMemberRadios is false
    const switchValues = useWatch({
      control,
      name:
        switchFieldNames.length > 0
          ? switchFieldNames.map(
              (fieldName) => `${name}[${memberIndex}].${fieldName}`
            )
          : [],
      disabled: showMemberRadios
    });

    const hasAnySwitchTrue =
      !showMemberRadios &&
      switchFieldNames.length > 0 &&
      Array.isArray(switchValues) &&
      switchValues.some((value) => value === true);

    // Auto-sync radio value based on switch values (when showMemberRadios is false)
    useEffect(() => {
      if (!showMemberRadios && switchFieldNames.length > 0) {
        const expectedRadioValue = hasAnySwitchTrue;

        // Only update if the current value is different from expected
        if (field.value !== expectedRadioValue) {
          field.onChange(expectedRadioValue);
        }
      }
    }, [showMemberRadios, switchFieldNames.length, hasAnySwitchTrue, field]);

    // Determine expansion - simplified logic
    let shouldExpand = true;

    if (!showMemberRadios) {
      // Check if only switch questions
      const hasOnlySwitches = checkIfOnlySwitchQuestions(subQuestionsConfig);

      if (hasOnlySwitches || switchFieldNames.length >= 3) {
        shouldExpand = true; // Always expanded for switch-only content
      } else if (
        field.value === undefined ||
        field.value === null ||
        field.value === ''
      ) {
        shouldExpand = true; // Initial state
      } else {
        shouldExpand = hasAnySwitchTrue; // Based on switch values
      }
    } else {
      // When showMemberRadios is true
      if (field.value === undefined || field.value === null || field.value === '') {
        shouldExpand = true; // Initial state
      } else {
        shouldExpand = field.value !== false; // Collapse only when "No"
      }
    }

    const shouldShowContent = !showMemberRadios || !isRadioNo;

    // Only modify config when needed (radio is "No")
    const finalFormConfig = useMemo(() => {
      if (!isRadioNo || !subQuestionsConfig) return subQuestionsConfig;

      // Disable all fields when radio is "No"
      const modifyField = (field: any): any => {
        if (INTERACTIVE_FIELD_TYPES.includes(field.type)) {
          return {
            ...field,
            componentProps: { ...field.componentProps, disabled: true },
            ...(field.fields && { fields: field.fields.map(modifyField) })
          };
        }
        if (field.fields) {
          return { ...field, fields: field.fields.map(modifyField) };
        }
        return field;
      };

      return subQuestionsConfig.map((section: any) =>
        section.type
          ? modifyField(section)
          : { ...section, fields: section.fields?.map(modifyField) }
      );
    }, [isRadioNo, subQuestionsConfig]);

    return (
      <AccordionSection
        key={memberIndex}
        itemValue={`membersAccordion-${member.member_id || memberIndex}`}
        restrictExpansion={isRadioNo}
        expanded={shouldExpand}
        formConfig={shouldShowContent ? finalFormConfig : []}
        classNames={{
          root: classes.root,
          control: clsx(classes.control, {
            [classes.nonInteractive]: noSubQuestions || isRadioNo
          }),
          label: classes.label,
          item: clsx(classes.item, {
            [classes.inactiveItem]: noSubQuestions || isRadioNo
          }),
          content: clsx(classes.content, {
            [classes.noContent]: noSubQuestions || !shouldShowContent
          }),
          chevron: clsx({
            [classes.hiddenChevron]: noSubQuestions || isRadioNo
          })
        }}
        {...((noSubQuestions || isRadioNo) && { chevron: null })}
        header={
          <Stack gap="xs" w="100%" onClick={(e) => e.stopPropagation()}>
            <Group
              justify="space-between"
              align="center"
              mr={!noSubQuestions ? 'sm' : 0}
            >
              <Stack gap="4">
                <Text
                  fz={18}
                  fw={600}
                  c={hasError || fieldState.error ? 'red.6' : undefined}
                >
                  {member.full_name}{' '}
                  <Text fz={14} c="secondary">
                    ({member.relation})
                  </Text>
                </Text>
              </Stack>
              <Stack>
                <Group
                  align="center"
                  gap="sm"
                  style={{ visibility: showMemberRadios ? 'visible' : 'hidden' }}
                >
                  <Radio.Group
                    size="md"
                    value={field.value === undefined ? '' : String(field.value)}
                    onChange={handleRadioChangeFactory(
                      field,
                      setValue,
                      clearErrors,
                      showMemberRadios,
                      subQuestionsConfig,
                      name,
                      memberIndex,
                      onSelectKey
                    )}
                    radiosWrapper="Group"
                    radiosWrapperProps={{
                      wrap: 'nowrap',
                      gap: 'xs'
                    }}
                    options={[
                      {
                        value: 'true',
                        label: 'Yes',
                        disabled: memberIdsWithDisease?.includes(
                          member.member_id || ''
                        ),
                        color: 'success'
                      },
                      {
                        value: 'false',
                        label: 'No',
                        disabled: memberIdsWithDisease?.includes(
                          member.member_id || ''
                        ),
                        color: 'danger'
                      }
                    ]}
                  />
                </Group>
                {fieldState.error && (
                  <Text fz={12} c="red.6">
                    {fieldState.error.message}
                  </Text>
                )}
              </Stack>
            </Group>
          </Stack>
        }
      />
    );
  }
);

MemberAccordionItem.displayName = 'MemberAccordionItem';
